import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Package, Type, Image, Hash, AlignVerticalJustifyStart, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd, Trash2, Maximize2, RotateCcw, AlignHorizontalJustifyEndIcon, AlignHorizontalJustifyCenterIcon, AlignHorizontalJustifyStartIcon } from 'lucide-react';

import { measureTextWidth } from '../../utils'

import ImageFiltersPanel from './ImageFiltersPanel';
import ImageEffectsPanel from './ImageEffectsPanel';

import '../../assets/css/leftpanel.css'

import {
  TextToolsPanel,
  CurveControl,
  FontSelect,
  SizeControl,
  TextStyleControls,
  TextColorControl,
} from './LeftPanelFields';

import blackTshirt from '../../assets/images/black-front.jpg'
import blueTshirt from '../../assets/images/blue-front.png'
import limeTshirt from '../../assets/images/lime-front.jpg'
import skyTshirt from '../../assets/images/sky-front.jpg'

import { getCurvedTextMetrics } from '../design/DesignOverlay';

const TextEffectsPanel = ({ textId, item, disabled, onUpdate, pendingTextRotationRef, flushTextRotationUpdate, updateTextRotationSmooth }) => {
  const strokeWidth = item?.strokeWidth || 0;
  const strokeColor = item?.strokeColor || '#000000';
  const shadowOffsetX = item?.shadowOffsetX || 0;
  const shadowOffsetY = item?.shadowOffsetY || 0;
  const shadowColor = item?.shadowColor || '#000000';
  const skewX = item?.skewX || 0;
  const skewY = item?.skewY || 0;
  const opacity = item?.opacity ?? 1;

  const dotStyle = (color) => ({
    display: 'inline-block',
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: color,
    border: '1px solid #ccc',
    position: 'relative',
    overflow: 'hidden',
    cursor: disabled ? 'not-allowed' : 'pointer',
    flexShrink: 0,
  });

  const hiddenColorInputStyle = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };

  const headerRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const labelWithDotStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };

  const sliderRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  };

  const subLabelStyle = {
    fontSize: 12,
    color: '#666',
    width: 56,
    flexShrink: 0,
  };

  const ResetButton = ({ onClick }) => (
    <button
      type="button"
      className="toggle-button"
      disabled={disabled}
      title="Reset"
      onClick={onClick}
    >
      <RotateCcw size={14} color="#80808082" />
    </button>
  );

  return (
    <>
      {/* Stroke */}
      <div className="control-group">
        <div className="control-label" style={headerRowStyle}>
          <span style={labelWithDotStyle}>
            Stroke
            <span style={dotStyle(strokeColor)}>
              <input
                type="color"
                value={strokeColor}
                disabled={disabled}
                onChange={(e) => onUpdate(textId, 'strokeColor', e.target.value)}
                style={hiddenColorInputStyle}
              />
            </span>
          </span>
          <ResetButton
            onClick={() => {
              onUpdate(textId, 'strokeWidth', 0);
              onUpdate(textId, 'strokeColor', '#000000');
            }}
          />
        </div>
        <div className="range-with-value">
          <input
            type="range"
            min="0"
            max="10"
            step="1"
            value={strokeWidth}
            disabled={disabled}
            onChange={(e) => {
              onUpdate(textId, 'strokeWidth', Number(e.target.value));
            }}
          />
          <div className="">{Math.round(strokeWidth)}</div>
        </div>
      </div>

      {/* Drop Shadow */}
      <div className="control-group">
        <div className="control-label" style={headerRowStyle}>
          <span style={labelWithDotStyle}>
            Drop Shadow
            <span style={dotStyle(shadowColor)}>
              <input
                type="color"
                value={shadowColor}
                disabled={disabled}
                onChange={(e) => onUpdate(textId, 'shadowColor', e.target.value)}
                style={hiddenColorInputStyle}
              />
            </span>
          </span>
          <ResetButton
            onClick={() => {
              onUpdate(textId, 'shadowOffsetX', 0);
              onUpdate(textId, 'shadowOffsetY', 0);
              onUpdate(textId, 'shadowColor', '#000000');
            }}
          />
        </div>

        <div style={{ ...sliderRowStyle, marginTop: 8 }}>
          <span style={subLabelStyle}>X-offset</span>
          <input
            type="range"
            min="-20"
            max="20"
            step="1"
            value={shadowOffsetX}
            disabled={disabled}
            style={{ flex: 1 }}
            onChange={(e) => onUpdate(textId, 'shadowOffsetX', Number(e.target.value))}
          />
          <div className="">{shadowOffsetX}</div>
        </div>

        <div style={{ ...sliderRowStyle, marginTop: 8 }}>
          <span style={subLabelStyle}>Y-offset</span>
          <input
            type="range"
            min="-20"
            max="20"
            step="1"
            value={shadowOffsetY}
            disabled={disabled}
            style={{ flex: 1 }}
            onChange={(e) => onUpdate(textId, 'shadowOffsetY', Number(e.target.value))}
          />
          <div className="">{shadowOffsetY}</div>
        </div>
      </div>

      {/* Rotation */}
      <div className="control-group">
        <div className="control-label" style={headerRowStyle}>
          <span>Rotation</span>
          <ResetButton
            onClick={() => {
              onUpdate(textId, 'rotation', 0);
            }}
          />
        </div>
        <div className="range-with-value">
          <input
            type="range"
            min="0"
            max="360"
            step="1"
            value={
              pendingTextRotationRef.current?.textId === textId
                ? pendingTextRotationRef.current.value
                : item?.rotation || 0
            }
            disabled={disabled}
            onChange={(e) => {
              updateTextRotationSmooth(
                textId,
                Number(e.target.value)
              );
            }}
            onPointerUp={() => {
              flushTextRotationUpdate();
            }}
          />
          <div className="">{Math.round(pendingTextRotationRef.current?.textId === textId ? pendingTextRotationRef.current.value : item?.rotation || 0)}</div>
        </div>
      </div>

      {/* Skew */}
      <div className="control-group">
        <div className="control-label" style={headerRowStyle}>
          <span>Skew</span>
          <ResetButton
            onClick={() => {
              onUpdate(textId, 'skewX', 0);
              onUpdate(textId, 'skewY', 0);
            }}
          />
        </div>

        <div style={{ ...sliderRowStyle, marginTop: 8 }}>
          <span style={subLabelStyle}>SkewX</span>
          <input
            type="range"
            min="0"
            max="180"
            step="1"
            value={skewX}
            disabled={disabled}
            style={{ flex: 1 }}
            onChange={(e) => onUpdate(textId, 'skewX', Number(e.target.value))}
          />
          <div className="">{skewX}</div>
        </div>

        <div style={{ ...sliderRowStyle, marginTop: 8 }}>
          <span style={subLabelStyle}>SkewY</span>
          <input
            type="range"
            min="0"
            max="180"
            step="1"
            value={skewY}
            disabled={disabled}
            style={{ flex: 1 }}
            onChange={(e) => onUpdate(textId, 'skewY', Number(e.target.value))}
          />
          <div className="">{skewY}</div>
        </div>
      </div>

      {/* Opacity */}
      <div className="control-group">
        <div className="control-label" style={headerRowStyle}>
          <span>Opacity</span>
          <ResetButton onClick={() => onUpdate(textId, 'opacity', 1)} />
        </div>
        <div className="range-with-value">
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={opacity}
            disabled={disabled}
            onChange={(e) => onUpdate(textId, 'opacity', Number(e.target.value))}
          />
          <div className="">{Number(opacity).toFixed(1)}</div>
        </div>
      </div>
    </>
  );
};

const LeftPanel = ({
  placementTexts,
  setPlacementTexts,
  placementImages,
  setPlacementImages,
  activePlacementKey,
  activeTab,
  setActiveTab,
  selectedColors,
  setSelectedColors,
  sizes,
  setSizes,
  placementList,
  activeTextId,
  setActiveTextId,
  editingFontSize,
  setEditingFontSize,
  fontSizeInput,
  setFontSizeInput,
  getMaxFontSize,
  editingRoleFontSize,
  setEditingRoleFontSize,
  roleFontSizeInput,
  setRoleFontSizeInput,
  colorInputRef,
  setShowCustomInput,
  showCustomInput,
  selectedPlacement,
  overlayCenter,
  activeImageId,
  setActiveImageId,
  setSelectedTextId,
  setSelectedImageId
}) => {

  const [nameValue, setNameValue] = useState('');
  const [numberValue, setNumberValue] = useState('');

  const [activeNameNumberRole, setActiveNameNumberRole] = useState('number');
  const [activeNameNumberId, setActiveNameNumberId] = useState(null);

  const safeActivePlacementKey = activePlacementKey || null;

  const fileInputRef = React.useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const uploadErrorTimeoutRef = useRef(null);
  const fontSizeFitTimeoutRef = useRef(null);
  const roleFontSizeFitTimeoutRef = useRef(null);

  const fontSizeRafRef = useRef(null);
  const roleFontSizeRafRef = useRef(null);

  const pendingFontSizeRef = useRef(null);
  const pendingRoleFontSizeRef = useRef(null);

  const textRotationRafRef = useRef(null);
  const imageRotationRafRef = useRef(null);

  const pendingTextRotationRef = useRef(null);
  const pendingImageRotationRef = useRef(null);

  const handleAddImageToPlacement = (file) => {
    if (!safeActivePlacementKey) return;

    if (!file) return;

    const newId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');

      // const defaultImgWidth = activePlacementKey === 'front-print' ? 150 : activePlacementKey === 'front-embroidery' ? 60 : activePlacementKey === 'back-embroidery' ? 60 : activePlacementKey === 'back-print' ? 100 : 60;
      // const defaultImgHeight = activePlacementKey === 'front-print' ? 150 : activePlacementKey === 'front-embroidery' ? 60 : activePlacementKey === 'back-embroidery' ? 60 : activePlacementKey === 'back-print' ? 100 : 60;
      // const defaultImgWidth =
      //   (overlayCenter?.x || 0) * 2
      // const defaultImgHeight =
      //   (overlayCenter?.y || 0) * 2

      const probe = new window.Image();
      probe.onload = () => {
        const aspectRatio =
          probe.naturalWidth && probe.naturalHeight
            ? probe.naturalWidth / probe.naturalHeight
            : 1;

        let imgWidth, imgHeight, imgX, imgY;

        const overlayW = (overlayCenter?.x || 0) * 2;
        const overlayH = (overlayCenter?.y || 0) * 2;

        let naturalW = probe.naturalWidth || 150;
        let naturalH = probe.naturalHeight || 150;

        if (naturalW > overlayW || naturalH > overlayH) {
          const scaleX = Number(overlayW - 5) / naturalW;
          const scaleY = overlayH / naturalH;
          const scale = Math.min(scaleX, scaleY);
          imgWidth = Math.round(naturalW * scale);
          imgHeight = Math.round(naturalH * scale);
        } else {
          imgWidth = Number(naturalW - 2);
          imgHeight = naturalH;
        }

        // Center the image in the overlay
        imgX = Math.max(0, Math.round((overlayW - imgWidth) / 2));
        imgY = Math.max(0, Math.round((overlayH - imgHeight) / 2));

        const imageItem = {
          id: newId,
          imageDataUrl: dataUrl,
          x: imgX,
          y: imgY,
          width: imgWidth,
          height: imgHeight,
          aspectRatio,
        };

        setPlacementImages((prev) => {
          const existing = prev?.[safeActivePlacementKey];
          const arr = Array.isArray(existing) ? existing : [];
          return {
            ...prev,
            [safeActivePlacementKey]: [...arr, imageItem],
          };
        });

        setActiveImageId?.(newId);
        setSelectedImageId?.(newId);
        setActiveTextId?.(null);
        setSelectedTextId?.(null);
        setActiveTab?.('images');
      };
      probe.src = dataUrl;
    };

    reader.readAsDataURL(file);
  };

  const updateTextAlign = (textId, textAlign) => {
    if (!activePlacementKey || !textId) return;

    setPlacementTexts(prev => ({
      ...prev,
      [activePlacementKey]: (prev[activePlacementKey] || []).map(item => {
        if (item.id !== textId) return item;

        return {
          ...item,
          textAlign: item.textAlign === textAlign ? null : textAlign,
        };
      }),
    }));
  };

  const updateTextTransform = (textId, textTransform) => {
    if (!activePlacementKey || !textId) return;

    setPlacementTexts(prev => ({
      ...prev,
      [activePlacementKey]: (prev[activePlacementKey] || []).map(item => {
        if (item.id !== textId) return item;

        return {
          ...item,
          textTransform: item.textTransform === textTransform ? null : textTransform,
        };
      }),
    }));
  };

  const updateFontSizeSmooth = (textId, value) => {
    if (!safeActivePlacementKey || !textId) return;

    pendingFontSizeRef.current = {
      textId,
      value,
    };

    if (fontSizeRafRef.current !== null) return;

    fontSizeRafRef.current = requestAnimationFrame(() => {
      fontSizeRafRef.current = null;

      const pending = pendingFontSizeRef.current;

      if (!pending) return;

      updateTextField(
        pending.textId,
        'fontSize',
        pending.value
      );
    });
  };


  const updateRoleFontSizeSmooth = (textId, value) => {
    if (!safeActivePlacementKey || !textId) return;

    pendingRoleFontSizeRef.current = {
      textId,
      value,
    };

    if (roleFontSizeRafRef.current !== null) return;

    roleFontSizeRafRef.current = requestAnimationFrame(() => {
      roleFontSizeRafRef.current = null;

      const pending = pendingRoleFontSizeRef.current;

      if (!pending) return;

      updateTextField(
        pending.textId,
        'fontSize',
        pending.value
      );
    });
  };

  const flushFontSizeUpdate = () => {
    if (fontSizeRafRef.current !== null) {
      cancelAnimationFrame(fontSizeRafRef.current);
      fontSizeRafRef.current = null;
    }

    const pending = pendingFontSizeRef.current;

    if (!pending) return;

    updateTextField(
      pending.textId,
      'fontSize',
      pending.value
    );

    pendingFontSizeRef.current = null;
  };


  const flushRoleFontSizeUpdate = () => {
    if (roleFontSizeRafRef.current !== null) {
      cancelAnimationFrame(roleFontSizeRafRef.current);
      roleFontSizeRafRef.current = null;
    }

    const pending = pendingRoleFontSizeRef.current;

    if (!pending) return;

    updateTextField(
      pending.textId,
      'fontSize',
      pending.value
    );

    pendingRoleFontSizeRef.current = null;
  };

  const updateTextRotationSmooth = (textId, value) => {
    if (!safeActivePlacementKey || !textId) return;

    pendingTextRotationRef.current = {
      textId,
      value,
    };

    if (textRotationRafRef.current !== null) return;

    textRotationRafRef.current = requestAnimationFrame(() => {
      textRotationRafRef.current = null;

      const pending = pendingTextRotationRef.current;

      if (!pending) return;

      updateTextField(
        pending.textId,
        'rotation',
        pending.value
      );
    });
  };

  const updateImageRotationSmooth = (imageId, value) => {
    if (!safeActivePlacementKey || !imageId) return;

    pendingImageRotationRef.current = {
      imageId,
      value,
    };

    if (imageRotationRafRef.current !== null) return;

    imageRotationRafRef.current = requestAnimationFrame(() => {
      imageRotationRafRef.current = null;

      const pending = pendingImageRotationRef.current;

      if (!pending) return;

      updateImageField(
        pending.imageId,
        'rotation',
        pending.value
      );
    });
  };

  const flushTextRotationUpdate = () => {
    if (textRotationRafRef.current !== null) {
      cancelAnimationFrame(textRotationRafRef.current);
      textRotationRafRef.current = null;
    }

    const pending = pendingTextRotationRef.current;

    if (!pending) return;

    updateTextField(
      pending.textId,
      'rotation',
      pending.value
    );

    pendingTextRotationRef.current = null;
  };

  const flushImageRotationUpdate = () => {
    if (imageRotationRafRef.current !== null) {
      cancelAnimationFrame(imageRotationRafRef.current);
      imageRotationRafRef.current = null;
    }

    const pending = pendingImageRotationRef.current;

    if (!pending) return;

    updateImageField(
      pending.imageId,
      'rotation',
      pending.value
    );

    pendingImageRotationRef.current = null;
  };

  const updateTextField = (textId, field, value) => {
    if (!safeActivePlacementKey || !textId) return;
    setPlacementTexts((prev) => {
      const items = prev?.[safeActivePlacementKey];
      const arr = Array.isArray(items) ? items : [];
      return {
        ...prev,
        [safeActivePlacementKey]: arr.map((t) => (t.id === textId ? { ...t, [field]: value } : t)),
      };
    });
  };

  const updateTextFlip = (textId, field, value) => {
    if (!safeActivePlacementKey || !textId) return;
    if (field !== 'flipH' && field !== 'flipV') return;
    updateTextField(textId, field, value);
  };

  const getOverlayDims = () => ({
    width: (overlayCenter?.x || 0) * 2,
    height: (overlayCenter?.y || 0) * 2,
  });

  const estimateTextBoxSize = (item) => {
    const fontSize = item?.fontSize || 16;
    const raw = (item?.text || '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/br>/gi, '\n')
      .replace(/\\n/g, '\n');
    const lines = raw.split('\n');
    // const longestLine = lines.reduce((max, l) => Math.max(max, l.length), 0) || 1;

    const width = lines.reduce(
      (max, line) => Math.max(max, measureTextWidth(line, fontSize, item?.fontFamily, item?.bold, item?.italic)),
      0
    ) || 1;

    return {
      // width: longestLine * fontSize * 0.55,
      width: width,
      height: lines.length * fontSize * 1.3,
    };
  };

  const findTextItem = (textId) => {
    if (!safeActivePlacementKey || !textId) return null;
    const items = placementTexts?.[safeActivePlacementKey];
    return Array.isArray(items) ? items.find((t) => t.id === textId) || null : null;
  };

  // Horizontal align of a text/name-number item relative to the print area.
  const alignTextHorizontal = (textId, align) => {
    const item = findTextItem(textId);
    if (!item) return;

    const { width: overlayWidth } = getOverlayDims();
    if (!overlayWidth) return;

    const { width: textWidth, height: textHeight } = estimateTextBoxSize(item);

    const bounds = getTextVisualBounds(
      item,
      textWidth,
      textHeight
    );

    let x = item.x || 0;

    if (align === 'left') {
      x = Math.max(
        bounds.offsetLeft,
        0
      );
    } else if (align === 'center') {
      const visualLeft =
        bounds.offsetLeft;

      const visualRight =
        textWidth + bounds.offsetRight;

      const visualWidth =
        visualRight - visualLeft;

      x =
        (overlayWidth - visualWidth) / 2 -
        visualLeft;

    } else if (align === 'right') {
      x =
        overlayWidth -
        textWidth -
        bounds.offsetRight - 3;

      x = Math.max(
        bounds.offsetLeft,
        x
      );
    }

    updateTextField(
      textId,
      'x',
      Math.round(x)
    );
  };

  const alignTextVertical = (textId, align) => {
    const item = findTextItem(textId);
    if (!item) return;

    const { height: overlayHeight } = getOverlayDims();
    const { height: itemHeight } = estimateTextBoxSize(item);

    let y = 0;

    if (align === 'center') {
      y = Math.max(0, Math.round((overlayHeight - itemHeight) / 2));

      if (item?.shadowOffsetY > 0) {
        y = y - (Math.abs(item?.shadowOffsetY || 0) / 2) + 4;
      }

      if (item?.shadowOffsetY < 0) {
        y = y + (Math.abs(item?.shadowOffsetY || 0) / 2);
      }
    } else if (align === 'bottom') {
      y = Math.max(0, Math.round(overlayHeight - itemHeight));

      if (item?.flipV === true) {
        if (item?.shadowOffsetY > 0) {
          y = y - (Math.abs(item?.shadowOffsetY || 0) / 2) - 20;
        }
        y = y + (Math.abs(item?.shadowOffsetY || 0) / 2) - 25;
      } else {
        if (item?.shadowOffsetY > 0) {
          y = y - (Math.abs(item?.shadowOffsetY || 0) / 2) - 5;
        }
      }
    } else {
      if (item?.flipV === true) {
        if (item?.shadowOffsetY > 0) {
          y = y - (Math.abs(item?.shadowOffsetY || 0) / 2) + 6;
        }
        y = y + (Math.abs(item?.shadowOffsetY || 0) / 2) + 10;
      } else {
        if (item?.shadowOffsetY < 0) {
          y = y + (Math.abs(item?.shadowOffsetY || 0) / 2) + 15;
        }
      }
    }

    updateTextField(textId, 'y', y);
  };

  const deleteTextItem = (textId) => {
    if (!safeActivePlacementKey || !textId) return;
    setPlacementTexts((prev) => {
      const items = Array.isArray(prev?.[safeActivePlacementKey]) ? prev[safeActivePlacementKey] : [];
      return {
        ...prev,
        [safeActivePlacementKey]: items.filter((t) => t.id !== textId),
      };
    });
  };

  const imageItemsForActivePlacement = useMemo(() => {
    if (!safeActivePlacementKey) return [];
    const items = placementImages?.[safeActivePlacementKey];
    return Array.isArray(items) ? items : [];
  }, [safeActivePlacementKey, placementImages]);

  const currentImage = useMemo(
    () => imageItemsForActivePlacement.find((img) => img.id === activeImageId) || null,
    [imageItemsForActivePlacement, activeImageId]
  );

  const getImageAspectRatio = (img) => {
    if (img?.aspectRatio && Number.isFinite(img.aspectRatio) && img.aspectRatio > 0) {
      return img.aspectRatio;
    }
    if (img?.width && img?.height) return img.width / img.height;
    return 1;
  };

  const updateImageField = (imageId, fieldName, value) => {
    if (!safeActivePlacementKey || !imageId) return;

    setPlacementImages((prev) => {
      const items = Array.isArray(prev?.[safeActivePlacementKey])
        ? [...prev[safeActivePlacementKey]]
        : [];

      const itemIndex = items.findIndex((img) => img.id === imageId);
      if (itemIndex === -1) return prev;

      items[itemIndex] = {
        ...items[itemIndex],
        [fieldName]: value, // ← This already supports all new effect properties!
      };

      return {
        ...prev,
        [safeActivePlacementKey]: items,
      };
    });
  };

  const deleteImageItem = (imageId) => {
    if (!safeActivePlacementKey || !imageId) return;
    setPlacementImages((prev) => {
      const items = Array.isArray(prev?.[safeActivePlacementKey]) ? prev[safeActivePlacementKey] : [];
      return {
        ...prev,
        [safeActivePlacementKey]: items.filter((img) => img.id !== imageId),
      };
    });
    setActiveImageId?.(null);
  };

  const alignImageHorizontal = (imageId, align) => {
    if (!safeActivePlacementKey || !imageId) return;
    const img = imageItemsForActivePlacement.find((i) => i.id === imageId);
    if (!img) return;

    const { width: overlayWidth } = getOverlayDims();
    const imgWidth = img?.width || 60;
    const imgHeight = img?.height || 60;

    const toRad = (deg) => ((deg || 0) * Math.PI) / 180;
    const r = toRad(img.rotation || 0);
    const sx = Math.tan(toRad(img.skewX || 0));
    const sy = Math.tan(toRad(img.skewY || 0));
    const c = Math.cos(r);
    const s = Math.sin(r);

    const A = c * (1 + sx * sy) - s * sy;
    const B = c * sx - s;

    const visualW = Math.abs(A) * imgWidth + Math.abs(B) * imgHeight;
    const offsetLeft = visualW / 2 - imgWidth / 2;
    const offsetRight = visualW / 2 - imgWidth / 2;

    let x = 0;
    if (align === 'center') {
      x = Math.max(0, Math.round((overlayWidth - imgWidth) / 2));
    } else if (align === 'right') {
      x = Math.max(0, Math.round(overlayWidth - imgWidth - offsetRight - 5));
    } else {
      x = Math.max(0, Math.round(offsetLeft));
    }

    const maxX = Math.max(0, overlayWidth - imgWidth);
    x = Math.min(Math.max(0, x), maxX);

    updateImageField(imageId, 'x', x);
  };

  const alignImageVertical = (imageId, align) => {
    if (!safeActivePlacementKey || !imageId) return;
    const img = imageItemsForActivePlacement.find((i) => i.id === imageId);
    if (!img) return;

    const { height: overlayHeight } = getOverlayDims();
    const imgWidth = img?.width || 60;
    const imgHeight = img?.height || 60;

    const toRad = (deg) => ((deg || 0) * Math.PI) / 180;
    const r = toRad(img.rotation || 0);
    const sx = Math.tan(toRad(img.skewX || 0));
    const sy = Math.tan(toRad(img.skewY || 0));
    const c = Math.cos(r);
    const s = Math.sin(r);

    const C = s * (1 + sx * sy) + c * sy;
    const D = s * sx + c;

    const visualH = Math.abs(C) * imgWidth + Math.abs(D) * imgHeight;
    const offsetTop = visualH / 2 - imgHeight / 2;
    const offsetBottom = visualH / 2 - imgHeight / 2;

    let y = 0;
    if (align === 'center') {
      y = Math.max(0, Math.round((overlayHeight - imgHeight) / 2));
    } else if (align === 'bottom') {
      y = Math.max(0, Math.round(overlayHeight - imgHeight - offsetBottom));
    } else {
      y = Math.max(0, Math.round(offsetTop));
    }

    const maxY = Math.max(0, overlayHeight - imgHeight);
    y = Math.min(Math.max(0, y), maxY);

    updateImageField(imageId, 'y', y);
  };

  const bestFitImage = (imageId) => {
    if (!safeActivePlacementKey || !imageId) return;
    const img = imageItemsForActivePlacement.find((i) => i.id === imageId);
    if (!img) return;

    const { width: overlayWidth, height: overlayHeight } = getOverlayDims();
    if (!overlayWidth || !overlayHeight) return;

    const aspectRatio = getImageAspectRatio(img);
    const toRad = (deg) => ((deg || 0) * Math.PI) / 180;
    const r = toRad(img.rotation);
    const sx = Math.tan(toRad(img.skewX));
    const sy = Math.tan(toRad(img.skewY));
    const c = Math.cos(r);
    const s = Math.sin(r);

    const A = c * (1 + sx * sy) - s * sy;
    const B = c * sx - s;
    const C = s * (1 + sx * sy) + c * sy;
    const D = s * sx + c;

    const padding = 2;
    const availableWidth = Math.max(1, overlayWidth - padding);
    const availableHeight = Math.max(1, overlayHeight - padding);

    const widthLimitedW = availableWidth / (Math.abs(A) + Math.abs(B) / aspectRatio);
    const heightLimitedW = availableHeight / (Math.abs(C) + Math.abs(D) / aspectRatio);

    const newWidth = Math.max(1, Math.min(widthLimitedW, heightLimitedW));
    const newHeight = Math.max(1, newWidth / aspectRatio);

    let newX = Math.max(0, Math.round((overlayWidth - newWidth) / 2));
    let newY = Math.max(0, Math.round((overlayHeight - newHeight) / 2));

    setPlacementImages((prev) => ({
      ...prev,
      [safeActivePlacementKey]: (prev[safeActivePlacementKey] || []).map((i) =>
        i.id === imageId
          ? { ...i, width: Math.round(newWidth), height: Math.round(newHeight), x: newX, y: newY }
          : i
      ),
    }));
  };

  const bestFitText = (textId) => {
    if (!safeActivePlacementKey || !textId) return;
    const item = findTextItem(textId);
    if (!item) return;

    const { width: overlayWidth, height: overlayHeight } = getOverlayDims();
    if (!overlayWidth || !overlayHeight) return;

    const raw = (item?.text || '')
      .replace(/<br\s*\/?>?/gi, '\n')
      .replace(/\r/g, '')
      .replace(/\t/g, '  ');
    const lines = raw.split('\n');
    const numLines = Math.max(lines.length, 1);

    const currentFontSize = item.fontSize || 16;
    if (item.curve) {
      const curveText = raw.replace(/\n/g, '');

      if (!curveText.trim()) return;

      const measureCurveBox = (fontSize) => {
        const ns = 'http://www.w3.org/2000/svg';

        const svg = document.createElementNS(ns, 'svg');
        const defs = document.createElementNS(ns, 'defs');
        const path = document.createElementNS(ns, 'path');
        const text = document.createElementNS(ns, 'text');
        const textPath = document.createElementNS(ns, 'textPath');

        const pathId = `best-fit-curve-${item.id}-${Date.now()}-${fontSize}`;

        const { pathD } = getCurvedTextMetrics(curveText, {
          fontSize,
          fontFamily: item.fontFamily || 'Arial',
          bold: !!item.bold,
          italic: !!item.italic,
          curveIntensity: item.curveIntensity || 100,
          direction: item.arcDirection || 'up',
        });

        path.setAttribute('id', pathId);
        path.setAttribute('d', pathD);
        path.setAttribute('fill', 'none');

        text.setAttribute('font-size', String(fontSize));
        text.setAttribute('font-family', item.fontFamily || 'Arial');
        text.setAttribute('font-weight', item.bold ? '700' : '400');
        text.setAttribute('font-style', item.italic ? 'italic' : 'normal');

        if (item.underline) {
          text.setAttribute('text-decoration', 'underline');
        }

        if (item.strokeWidth) {
          text.setAttribute(
            'stroke',
            item.strokeColor || '#000000'
          );

          text.setAttribute(
            'stroke-width',
            String(item.strokeWidth)
          );
        }

        if ((item.arcDirection || 'up') === 'down') {
          text.setAttribute('letter-spacing', '0.09em');
        }

        textPath.setAttribute(
          'href',
          `#${pathId}`
        );

        textPath.setAttribute(
          'startOffset',
          '50%'
        );

        textPath.setAttribute(
          'text-anchor',
          'middle'
        );

        textPath.textContent = curveText;

        text.appendChild(textPath);
        defs.appendChild(path);

        svg.appendChild(defs);
        svg.appendChild(text);

        svg.style.position = 'absolute';
        svg.style.left = '-100000px';
        svg.style.top = '-100000px';
        svg.style.visibility = 'hidden';

        document.body.appendChild(svg);

        let bbox;

        try {
          bbox = textPath.getBBox();
        } catch {
          bbox = {
            x: 0,
            y: -fontSize * 0.3,
            width: curveText.length * fontSize * 0.6,
            height: fontSize * 1.2,
          };
        }

        document.body.removeChild(svg);

        const curvePadding = 10;
        const verticalPadding = fontSize * 0.4;

        let width =
          Math.max(
            bbox.width,
            curveText.length * fontSize * 0.55
          ) +
          curvePadding * 2;

        let height =
          bbox.height +
          verticalPadding * 2;

        if ((item.arcDirection || 'up') === 'down') {
          height += 8;
        }

        return {
          width: Math.max(100, width),
          height: Math.max(50, height),
        };
      };

      const getCurveVisualBounds = (fontSize) => {
        const box = measureCurveBox(fontSize);

        const bounds = getTextVisualBounds(
          item,
          box.width,
          box.height
        );

        return {
          ...box,
          ...bounds,
        };
      };

      const fitPadding = 4;

      const availableWidth = Math.max(
        1,
        overlayWidth - fitPadding * 2
      );

      const availableHeight = Math.max(
        1,
        overlayHeight - fitPadding * 2
      );

      const currentCurve = getCurveVisualBounds(
        currentFontSize
      );

      const currentVisualWidth =
        currentCurve.visualW +
        Math.max(
          0,
          currentCurve.offsetLeft +
          currentCurve.offsetRight -
          (currentCurve.visualW - currentCurve.width)
        );

      const currentVisualHeight =
        currentCurve.visualH +
        Math.max(
          0,
          currentCurve.offsetTop +
          currentCurve.offsetBottom -
          (currentCurve.visualH - currentCurve.height)
        );

      const widthScale =
        availableWidth /
        Math.max(1, currentVisualWidth);

      const heightScale =
        availableHeight /
        Math.max(1, currentVisualHeight);

      const scale = Math.min(
        widthScale,
        heightScale
      );

      let newFontSize = Math.max(
        8,
        Math.min(
          200,
          Math.floor(
            currentFontSize *
            scale *
            0.98
          )
        )
      );

      for (let i = 0; i < 4; i += 1) {
        const measured =
          getCurveVisualBounds(newFontSize);

        const visualWidth =
          measured.visualW +
          Math.max(
            0,
            measured.offsetLeft +
            measured.offsetRight -
            (measured.visualW - measured.width)
          );

        const visualHeight =
          measured.visualH +
          Math.max(
            0,
            measured.offsetTop +
            measured.offsetBottom -
            (measured.visualH - measured.height)
          );

        if (
          visualWidth <= availableWidth &&
          visualHeight <= availableHeight
        ) {
          break;
        }

        const fitScale = Math.min(
          availableWidth /
          Math.max(1, visualWidth),
          availableHeight /
          Math.max(1, visualHeight)
        );

        const nextFontSize = Math.max(
          8,
          Math.floor(
            newFontSize *
            fitScale *
            0.98
          )
        );

        if (nextFontSize >= newFontSize) {
          break;
        }

        newFontSize = nextFontSize;
      }

      const finalCurve =
        getCurveVisualBounds(newFontSize);

      let newX = Math.round(
        (
          overlayWidth -
          finalCurve.width -
          finalCurve.offsetRight +
          finalCurve.offsetLeft
        ) / 2
      );

      let newY = Math.round(
        (
          overlayHeight -
          finalCurve.height -
          finalCurve.offsetBottom +
          finalCurve.offsetTop
        ) / 2
      );

      newX = Math.max(
        finalCurve.offsetLeft,
        Math.min(
          newX,
          overlayWidth -
          finalCurve.width -
          finalCurve.offsetRight
        )
      );

      newY = Math.max(
        finalCurve.offsetTop,
        Math.min(
          newY,
          overlayHeight -
          finalCurve.height -
          finalCurve.offsetBottom
        )
      );

      setPlacementTexts((prev) => {
        const items = Array.isArray(
          prev?.[safeActivePlacementKey]
        )
          ? [...prev[safeActivePlacementKey]]
          : [];

        return {
          ...prev,
          [safeActivePlacementKey]: items.map((t) =>
            t.id === textId
              ? {
                ...t,
                fontSize: newFontSize,
                textAlign: 'center',
                x: Math.round(newX),
                y: Math.round(newY),
              }
              : t
          ),
        };
      });

      return;
    }

    const isFrontBackPrint = safeActivePlacementKey === 'front-print' || safeActivePlacementKey === 'back-print';

    const lineHeightFactor = 1.25;

    const currentLineWidths = lines.map((line) =>
      measureTextWidth(line, currentFontSize, item.fontFamily, item.bold, item.italic)
    );
    const maxCurrentLineWidth = Math.max(...currentLineWidths, 1);
    const currentTotalHeight = numLines * currentFontSize * lineHeightFactor;

    const ox = Math.abs(item?.shadowOffsetX || 0);
    const oy = Math.abs(item?.shadowOffsetY || 0);
    const hasShadow = ox > 0 || oy > 0;

    const padding = 2;

    let innerWidth = overlayWidth - padding * 2;
    let innerHeight = overlayHeight - padding * 2;

    if (isFrontBackPrint) {
      const toRad = (deg) => (deg * Math.PI) / 180;
      const r = toRad(item?.rotation || 0);
      const hF = item?.flipH ? -1 : 1;
      const vF = item?.flipV ? -1 : 1;
      const ta = Math.tan(toRad(item?.skewX || 0));
      const tb = Math.tan(toRad(item?.skewY || 0));
      const c = Math.cos(r);
      const s = Math.sin(r);

      const A = c * (1 + ta * tb) - s * (vF * tb);
      const B = c * (hF * ta) - s * (vF);
      const C = s * (hF * (1 + ta * tb)) + c * (vF * tb);
      const D = s * (hF * ta) + c * (vF);

      const shadowW = hasShadow ? (Math.abs(A) * ox + Math.abs(B) * oy) : 0;
      const shadowH = hasShadow ? (Math.abs(C) * ox + Math.abs(D) * oy) : 0;

      const availableW = Math.max(1, innerWidth - shadowW - 8);
      const availableH = Math.max(1, innerHeight - shadowH - 8);

      const baseW = maxCurrentLineWidth;
      const baseH = currentTotalHeight;

      const sW = availableW / (Math.abs(A) * baseW + Math.abs(B) * baseH);
      const sH = availableH / (Math.abs(C) * baseW + Math.abs(D) * baseH);
      const scale = Math.max(0.1, Math.min(sW, sH));

      let newFontSize = Math.max(8, Math.min(200, Math.floor(currentFontSize * scale)));

      const newLineWidths = lines.map((line) =>
        measureTextWidth(line, newFontSize, item.fontFamily, item.bold, item.italic)
      );
      const newMaxLineWidth = Math.max(...newLineWidths, 1);
      const newTotalHeight = numLines * newFontSize * lineHeightFactor;

      const finalW = Math.abs(A) * newMaxLineWidth + Math.abs(B) * newTotalHeight + shadowW;
      const finalH = Math.abs(C) * newMaxLineWidth + Math.abs(D) * newTotalHeight + shadowH;

      if (finalW > innerWidth || finalH > innerHeight) {
        const sW2 = (innerWidth - 2) / Math.max(1, finalW);
        const sH2 = (innerHeight - 2) / Math.max(1, finalH);
        const extraScale = Math.min(sW2, sH2);
        newFontSize = Math.max(8, Math.floor(newFontSize * extraScale));
      }

      const finalLineWidths2 = lines.map((line) =>
        measureTextWidth(line, newFontSize, item.fontFamily, item.bold, item.italic)
      );
      const finalMaxLineWidth2 = Math.max(...finalLineWidths2, 1);
      const finalTotalHeight2 = numLines * newFontSize * lineHeightFactor;

      const newX = Math.max(
        0,
        Math.round((overlayWidth - finalMaxLineWidth2) / 2)
      );
      const newY = Math.max(
        0,
        Math.round((overlayHeight - finalTotalHeight2) / 2)
      );

      setPlacementTexts((prev) => {
        const items = Array.isArray(prev?.[safeActivePlacementKey])
          ? [...prev[safeActivePlacementKey]]
          : [];
        return {
          ...prev,
          [safeActivePlacementKey]: items.map((t) =>
            t.id === textId
              ? {
                ...t,
                fontSize: newFontSize,
                textAlign: 'center',
                x: newX,
                y: newY,
              }
              : t
          ),
        };
      });

      return;
    }

    let maxFitWidth = innerWidth;
    let maxFitHeight = innerHeight;

    if (hasShadow) {
      maxFitWidth = maxFitWidth - ox - 6;
      maxFitHeight = maxFitHeight - oy - 6;
    }

    const visualW = maxCurrentLineWidth;
    const visualH = currentTotalHeight;

    const widthScale = maxFitWidth / Math.max(1, visualW);
    const heightScale = maxFitHeight / Math.max(1, visualH);
    const scale = Math.min(widthScale, heightScale);

    let newFontSize = Math.max(8, Math.min(200, Math.floor(currentFontSize * scale)));

    const newLineWidths = lines.map((line) =>
      measureTextWidth(line, newFontSize, item.fontFamily, item.bold, item.italic)
    );
    const newMaxLineWidth = Math.max(...newLineWidths, 1);
    const newTotalHeight = numLines * newFontSize * lineHeightFactor;

    const newX = Math.max(
      0,
      Math.round((overlayWidth - newMaxLineWidth) / 2)
    );
    const newY = Math.max(
      0,
      Math.round((overlayHeight - newTotalHeight) / 2)
    );

    setPlacementTexts((prev) => {
      const items = Array.isArray(prev?.[safeActivePlacementKey])
        ? [...prev[safeActivePlacementKey]]
        : [];
      return {
        ...prev,
        [safeActivePlacementKey]: items.map((t) =>
          t.id === textId
            ? {
              ...t,
              fontSize: newFontSize,
              textAlign: 'center',
              x: newX,
              y: newY,
            }
            : t
        ),
      };
    });
  };

  const getTextTransformMatrix = (t) => {
    const toRad = (deg) => ((deg || 0) * Math.PI) / 180;
    const r = toRad(t.rotation || 0);
    const hF = t.flipH ? -1 : 1;
    const vF = t.flipV ? -1 : 1;
    const ta = Math.tan(toRad(t.skewX || 0));
    const tb = Math.tan(toRad(t.skewY || 0));
    const c = Math.cos(r);
    const s = Math.sin(r);

    const A = c * hF * (1 + ta * tb) - s * vF * tb;
    const B = c * hF * ta - s * vF;
    const C = s * hF * (1 + ta * tb) + c * vF * tb;
    const D = s * hF * ta + c * vF;

    return { A, B, C, D };
  };

  const getTextVisualBounds = (t, w, h) => {
    const { A, B, C, D } = getTextTransformMatrix(t);

    const visualW = Math.abs(A) * w + Math.abs(B) * h;
    const visualH = Math.abs(C) * w + Math.abs(D) * h;

    const ox = t.shadowOffsetX || 0;
    const oy = t.shadowOffsetY || 0;
    const hasShadow = !!ox || !!oy;
    const blur = hasShadow ? 4 : 0;
    const shx = hasShadow ? A * ox + B * oy : 0;
    const shy = hasShadow ? C * ox + D * oy : 0;
    const shLeft = hasShadow ? Math.max(0, -shx) : 0;
    const shRight = hasShadow ? Math.max(0, shx) : 0;
    const shTop = hasShadow ? Math.max(0, -shy) : 0;
    const shBottom = hasShadow ? Math.max(0, shy) : 0;

    const align = t.textAlign || 'left';
    const alignShift = w * 0.08;

    let extraLeft = 0;
    let extraRight = 0;

    if (align === 'left') {
      extraLeft = alignShift;
    } else if (align === 'right') {
      extraRight = alignShift;
    }

    return {
      offsetLeft: visualW / 2 - w / 2 + shLeft + blur + extraLeft,
      offsetRight: visualW / 2 - w / 2 + shRight + blur + extraRight,
      offsetTop: visualH / 2 - h / 2 + shTop + blur,
      offsetBottom: visualH / 2 - h / 2 + shBottom + blur,
      visualW,
      visualH,
    };
  };

  const checkIfTextOutOfBounds = (textItem) => {
    if (!textItem) return false;

    const dims = getOverlayDims();
    if (!dims || dims.width <= 0 || dims.height <= 0) return false;

    const x = textItem.x || 0;
    const y = textItem.y || 0;

    const raw = (textItem.text || '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/br>/gi, '\n')
      .replace(/\\n/g, '\n');

    if (!raw.trim()) return false;

    const lines = raw.split('\n');
    const fontSize = textItem.fontSize || 16;
    const lineHeightFactor = 1.25;

    const maxLineWidth = lines.reduce(
      (max, line) => Math.max(max, measureTextWidth(line, fontSize, textItem.fontFamily, textItem.bold, textItem.italic)),
      0
    ) || 1;

    const totalHeight = lines.length * fontSize * lineHeightFactor;

    const { offsetLeft, offsetRight, offsetTop, offsetBottom } = getTextVisualBounds(
      textItem,
      maxLineWidth,
      totalHeight
    );

    const isOutside =
      x - offsetLeft < 0 ||
      y - offsetTop < 0 ||
      x + maxLineWidth + offsetRight > dims.width ||
      y + totalHeight + offsetBottom > dims.height;

    return isOutside;
  };
  const handleFontSizeDragEnd = (textId) => {
    if (!safeActivePlacementKey || !textId) return;

    if (fontSizeFitTimeoutRef.current) {
      clearTimeout(fontSizeFitTimeoutRef.current);
    }

    fontSizeFitTimeoutRef.current = setTimeout(() => {
      requestAnimationFrame(() => {
        const items = placementTexts?.[safeActivePlacementKey];
        if (!Array.isArray(items)) return;

        const textItem = items.find((t) => t.id === textId);

        if (!textItem || textItem.role) return;

        if (!checkIfTextOutOfBounds(textItem)) return;

        const dims = getOverlayDims();

        if (!dims || dims.width <= 0 || dims.height <= 0) return;

        const raw = (textItem.text || '')
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/br>/gi, '\n')
          .replace(/\\n/g, '\n');

        if (!raw.trim()) return;

        const lines = raw.split('\n');
        const lineHeightFactor = 1.25;

        let fontSize = (textItem.fontSize - 2) || 16;

        while (fontSize > 8) {
          const maxLineWidth =
            lines.reduce(
              (max, line) =>
                Math.max(
                  max,
                  measureTextWidth(line, fontSize, textItem.fontFamily, textItem.bold, textItem.italic)
                ),
              0
            ) || 1;

          const totalHeight =
            lines.length * fontSize * lineHeightFactor;

          const testItem = {
            ...textItem,
            fontSize,
          };

          const {
            offsetLeft,
            offsetRight,
            offsetTop,
            offsetBottom,
          } = getTextVisualBounds(
            testItem,
            maxLineWidth,
            totalHeight
          );

          const fits =
            textItem.x - offsetLeft >= 0 &&
            textItem.y - offsetTop >= 0 &&
            textItem.x + maxLineWidth + offsetRight <= dims.width &&
            textItem.y + totalHeight + offsetBottom <= dims.height;

          if (fits) break;

          fontSize -= 2;
        }

        setPlacementTexts((prev) => {
          const items = Array.isArray(prev?.[safeActivePlacementKey])
            ? [...prev[safeActivePlacementKey]]
            : [];

          return {
            ...prev,
            [safeActivePlacementKey]: items.map((t) =>
              t.id === textId
                ? {
                  ...t,
                  fontSize: Math.max(8, fontSize),
                }
                : t
            ),
          };
        });
      });
    }, 30);
  };


  const handleRoleFontSizeDragEnd = (roleId) => {
    if (!safeActivePlacementKey || !roleId) return;

    if (roleFontSizeFitTimeoutRef.current) {
      clearTimeout(roleFontSizeFitTimeoutRef.current);
    }

    roleFontSizeFitTimeoutRef.current = setTimeout(() => {
      requestAnimationFrame(() => {
        const items = placementTexts?.[safeActivePlacementKey];

        if (!Array.isArray(items)) return;

        const selectedItem = items.find((t) => t.id === roleId);

        if (!selectedItem || !selectedItem.role) return;

        const dims = getOverlayDims();

        if (!dims || dims.width <= 0 || dims.height <= 0) return;

        const lineHeightFactor = 1.25;

        const itemFitsAtFontSize = (item, fontSize) => {
          if (!item) return true;

          const raw = (item.text || '')
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/br>/gi, '\n')
            .replace(/\\n/g, '\n')
            .replace(/\r/g, '');

          if (!raw.trim()) return true;

          const lines = raw.split('\n');

          const maxLineWidth =
            lines.reduce(
              (max, line) =>
                Math.max(
                  max,
                  measureTextWidth(line, fontSize, item.fontFamily, item.bold, item.italic)
                ),
              0
            ) || 1;

          const totalHeight =
            lines.length * fontSize * lineHeightFactor;

          const testItem = {
            ...item,
            fontSize,
          };

          const { offsetLeft, offsetRight, offsetTop, offsetBottom } = getTextVisualBounds(testItem, maxLineWidth, totalHeight);

          const x = Number(item.x) || 0;
          const y = Number(item.y) || 0;

          return (
            item.x - offsetLeft >= 0 &&
            item.y - offsetTop >= 0 &&
            item.x + maxLineWidth + offsetRight <= dims.width &&
            item.y + totalHeight + offsetBottom <= dims.height
          );
        };
        const pending = pendingRoleFontSizeRef.current;

        let fontSize =
          pending?.textId === roleId
            ? Number(pending.value)
            : Number(selectedItem.fontSize);
        if (itemFitsAtFontSize(selectedItem, fontSize)) {
          pendingRoleFontSizeRef.current = null;
          return;
        }

        while (
          fontSize > 8 &&
          !itemFitsAtFontSize(selectedItem, fontSize)
        ) {
          fontSize -= 1;
        }

        fontSize = Math.max(8, fontSize);

        setPlacementTexts((prev) => {
          const arr = Array.isArray(prev?.[safeActivePlacementKey])
            ? [...prev[safeActivePlacementKey]]
            : [];

          return {
            ...prev,
            [safeActivePlacementKey]: arr.map((t) => {
              if (t.id !== roleId) {
                return t;
              }

              return {
                ...t,
                fontSize,
              };
            }),
          };
        });

        pendingRoleFontSizeRef.current = null;
      });
    }, 30);
  };


  const upsertRoleItem = (items, role, text, defaults) => {
    const index = items.findIndex((item) => item.role === role);

    if (index !== -1) {
      items[index] = {
        ...items[index],
        text,
      };
    } else {
      items.push({
        id: `${Date.now()}-${role}`,
        role,
        text,
        ...defaults,
      });
    }
  };

  const defaultTextState = useMemo(() => ({
    id: null,
    text: '',
    fontSize: (activePlacementKey === 'front-print' || activePlacementKey === 'back-print') ? 30 : 20,
    fontFamily: 'Arial',
    color: '#ffffff',
    bold: false,
    italic: false,
    underline: false,
    textAlign: "left",
    textTransform: "none",
    flipH: false,
    flipV: false,
    rotation: 0,
  }), [activePlacementKey]);

  const currentText = useMemo(() => {
    if (!safeActivePlacementKey) return defaultTextState;

    const items = placementTexts?.[safeActivePlacementKey];
    if (!Array.isArray(items)) return defaultTextState;

    const found = items.find((t) => t.id === activeTextId && !t.role);
    return found || defaultTextState;
  }, [safeActivePlacementKey, defaultTextState, placementTexts, activeTextId]);

  const handleColorSelect = (color) => {
    setSelectedColors([color]);
  };

  const hasPlacementForCurrentSide = useMemo(() => {
    if (!selectedPlacement) return false;
    return placementList?.some(item => item.placement === selectedPlacement) ?? false;
  }, [selectedPlacement, placementList]);

  const tabs = [
    { id: 'product', label: 'Product', icon: Package },
    { id: 'text', label: 'Plain Text', icon: Type },
    { id: 'images', label: 'Images', icon: Image },
    { id: 'number', label: 'Name Number', icon: Hash },
  ];

  const nameNumberTextsForActivePlacement = useMemo(() => {
    if (!safeActivePlacementKey) return { nameItem: null, numberItem: null };
    const items = placementTexts?.[safeActivePlacementKey];
    if (!Array.isArray(items)) return { nameItem: null, numberItem: null };

    const arr = [...(placementTexts?.[safeActivePlacementKey] || [])];
    const nameItem = arr.find(item => item.role === "name");
    const numberItem = arr.find(item => item.role === "number");

    if (activeNameNumberRole === "name") {
      setActiveNameNumberId(nameItem?.id || null);
    } else {
      setActiveNameNumberId(numberItem?.id || null);
    }
    return { nameItem, numberItem };
  }, [safeActivePlacementKey, placementTexts]);

  useEffect(() => {
    if (!safeActivePlacementKey) {
      setNameValue('');
      setNumberValue('');
      return;
    }

    setNameValue(nameNumberTextsForActivePlacement.nameItem?.text || '');
    setNumberValue(nameNumberTextsForActivePlacement.numberItem?.text || '');
  }, [
    safeActivePlacementKey,
    nameNumberTextsForActivePlacement,
  ]);

  useEffect(() => {
    if (!safeActivePlacementKey || !activeTextId) return;
    const items = placementTexts?.[safeActivePlacementKey];
    if (!Array.isArray(items)) return;

    const found = items.find((t) => t.id === activeTextId);
    if (found?.role) {
      setActiveNameNumberRole(found.role);
      setActiveNameNumberId(found.id);
    }
  }, [activeTextId, safeActivePlacementKey, placementTexts]);

  const canApplyNameNumber = useMemo(() => {
    if (!safeActivePlacementKey) return false;
    return Boolean(nameValue.trim() || numberValue.trim());
  }, [safeActivePlacementKey, nameValue, numberValue]);


  const activeRoleItem =
    placementTexts?.[safeActivePlacementKey]?.find(
      item => item.id === activeNameNumberId
    ) || null;

  const activeRoleItemId = activeRoleItem?.id || null;

  const currentRoleText = activeRoleItem || {
    ...defaultTextState,
    fontSize: activeNameNumberRole === 'number' ? 70 : 40,
    bold: activeNameNumberRole === 'number',
    textAlign: 'center',
  };

  const isNumberText = activeNameNumberRole === 'number';
  const hasMultipleLines = (currentText.text || '').includes('\n');

  const plainTextItemForPlacement = useMemo(() => {
    if (!safeActivePlacementKey) return null;
    const items = placementTexts?.[safeActivePlacementKey];
    if (!Array.isArray(items)) return null;

    const existingActivePlain = items.find((t) => t.id === activeTextId && !t.role);
    if (existingActivePlain) return existingActivePlain;

    return items.find((t) => !t.role) || null;
  }, [safeActivePlacementKey, placementTexts, activeTextId]);

  const tshirtColors = [
    { color: '#000000', image: blackTshirt },
    { color: '#0255A1', image: blueTshirt },
    { color: '#99DFF9', image: skyTshirt },
    { color: '#85CD6A', image: limeTshirt }
  ];

  const DEFAULT_TEXT_COLORS = ['#FFFFFF', '#000000', '#FF3B30', '#0A66FF', '#FFD400', '#00A86B', '#FF7A00'];

  const [customColors, setCustomColors] = useState([]);
  const allTextColors = [...DEFAULT_TEXT_COLORS, ...customColors];

  useEffect(() => {
    return () => {
      if (fontSizeRafRef.current !== null) {
        cancelAnimationFrame(fontSizeRafRef.current);
      }

      if (roleFontSizeRafRef.current !== null) {
        cancelAnimationFrame(roleFontSizeRafRef.current);
      }

      if (textRotationRafRef.current !== null) {
        cancelAnimationFrame(textRotationRafRef.current);
      }

      if (imageRotationRafRef.current !== null) {
        cancelAnimationFrame(imageRotationRafRef.current);
      }
    };
  }, []);

  return (
    <div className='tshirt-customizer'>
      <div className="sidebar">
        <div className="sidebar-container">
          {tabs?.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab?.id;
            return (
              <button
                key={tab?.id}
                onClick={() => {
                  setActiveTab(tab?.id);
                  if (tab?.id === 'number') {
                    setActiveTextId(null);
                  }

                  if (tab?.id === 'text') {
                    setActiveTextId(plainTextItemForPlacement?.id ?? null);
                  }
                }}
                className={`sidebar-button ${isActive ? 'active' : ''} ${!hasPlacementForCurrentSide && tab?.label !== "Product" ? '' : ''}`}
                // disabled={!hasPlacementForCurrentSide && tab?.label !== "Product"}
                title={tab?.label}
              >
                <Icon size={24} />
                <span>
                  {tab?.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="main-content">
        {(!hasPlacementForCurrentSide && activeTab !== "product") && <p className="error-message-placement-selection">Please select print area</p>}
        <div className={`content-wrapper  ${!hasPlacementForCurrentSide && activeTab !== "product" ? 'disabled-panel' : ''}`}>
          {
            activeTab === 'product' &&

            <div className="product-section">
              <div className="product-header">
                <h2 className="product-title">
                </h2>
              </div>
              <div className="preview-container">
                <div className="preview-image">
                  <img
                    src={(() => {
                      const selected = Array.isArray(selectedColors) ? selectedColors[0] : null;
                      const map = {
                        '#000000': blackTshirt,
                        '#0255A1': blueTshirt,
                        '#99DFF9': skyTshirt,
                        '#85CD6A': limeTshirt,
                      };
                      return map[selected] || blackTshirt;
                    })()}
                    alt="T-shirt preview"
                    className="tshirt-svg"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </div>
                <span className='preview-product-name'>Under Armour 1389864 - Drive Quarter-Zip Pullover</span>
              </div>
              <div className="color-section">
                <div>
                  <h3 className="section-title">
                    Choose color, size and quantities
                  </h3>
                  <div className="color-grid">
                    {
                      tshirtColors?.map((item, index) => {
                        return (
                          <>
                            <div key={index} onClick={() => handleColorSelect(item?.color)} className={`color-options ${selectedColors?.includes(item?.color) ? 'selected' : ''}`}>
                              <img src={item?.image} alt="tshirt-customizer" />
                            </div>
                            {uploadError && (
                              <p className="error-message" style={{ marginTop: 5 }}>{uploadError}</p>
                            )}
                          </>
                        )
                      })
                    }
                  </div>
                </div>
              </div>
            </div>

          }

          {activeTab === 'text' && (
            <div className="text-section">
              <div className="text-editor-header">
                <div>
                  <h3 className="section-title">Plain Text</h3>
                  <div className="text-editor-subtitle">
                    Style your typography for the selected placement.
                  </div>
                </div>
              </div>
              <div className="text-editor-body">
                <div className="text-editor-form">
                  <div className="form-group">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                      <label className="form-label">Text Content</label>
                      <button
                        type="button"
                        className="border-button"
                        onClick={() => {
                          if (!safeActivePlacementKey) return;
                          const newId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
                          // Estimate text dimensions to visually center the element
                          const estFontSize = (safeActivePlacementKey === 'front-print' || safeActivePlacementKey === 'back-print') ? 30 : 20;
                          const estCharWidth = estFontSize * 0.55;
                          const estTextWidth = 'Text'.length * estCharWidth;
                          const estTextHeight = estFontSize * 1.3;
                          const centerX = overlayCenter?.x || 0;
                          const centerY = overlayCenter?.y || 0;
                          const newItem = {
                            id: newId,
                            text: 'Text',
                            fontSize: estFontSize,
                            fontFamily: 'Arial',
                            color: '#ffffff',
                            bold: false,
                            italic: false,
                            underline: false,
                            textAlign: 'center',
                            textTransform: 'none',
                            x: Math.max(0, centerX - Math.round(estTextWidth / 2)),
                            y: Math.max(0, centerY - Math.round(estTextHeight / 2)),
                            flipH: false,
                            flipV: false,
                            rotation: 0,
                          };
                          setPlacementTexts((prev) => {
                            const existing = prev?.[safeActivePlacementKey];
                            const arr = Array.isArray(existing) ? existing : [];
                            return {
                              ...prev,
                              [safeActivePlacementKey]: [...arr, newItem],
                            };
                          });
                          setActiveTextId(newId);
                          setSelectedTextId?.(newId);
                          setActiveImageId?.(null);
                          setSelectedImageId?.(null);
                          setActiveTab?.('text');
                        }}
                        disabled={!safeActivePlacementKey}
                      >
                        + Add Text
                      </button>
                    </div>
                    <div className="form-group">
                      <textarea
                        className="form-textarea text-editor-textarea"
                        rows="5"
                        maxLength={30}
                        placeholder={
                          safeActivePlacementKey
                            ? activeTextId
                              ? 'Type your text...'
                              : 'Add or select a text item'
                            : 'Select a placement first'
                        }
                        value={currentText.text}
                        disabled={!safeActivePlacementKey || !activeTextId}
                        onChange={(e) => {
                          if (!safeActivePlacementKey || !activeTextId) return;

                          updateTextField(
                            activeTextId,
                            'text',
                            e.target.value.slice(0, 30)
                          );
                        }}
                      />
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          marginBottom: 8,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            color: '#666',
                          }}
                        >
                          {currentText.text?.length || 0}/30
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="editor-row">
                    <TextToolsPanel
                      textId={activeTextId}
                      item={currentText}
                      disabled={!activeTextId}
                      onAlignHorizontal={alignTextHorizontal}
                      onAlignVertical={alignTextVertical}
                      onFlip={updateTextFlip}
                      onBestFit={bestFitText}
                      onDelete={() => {
                        if (!activeTextId) return;
                        deleteTextItem(activeTextId);
                        setActiveTextId(null);
                      }}
                    />
                  </div>
                  <div className="editor-row">
                    <CurveControl
                      textId={activeTextId}
                      curve={currentText?.curve || false}
                      arcDirection={currentText.arcDirection || 'up'}
                      disabled={!activeTextId}
                      onToggleCurve={(textId, checked) => updateTextField(textId, 'curve', checked)}
                      onSetDirection={(textId, direction) => updateTextField(textId, 'arcDirection', direction)}
                    />
                  </div>
                  <div className="editor-row">
                    <FontSelect
                      value={currentText.fontFamily}
                      disabled={!safeActivePlacementKey || !activeTextId}
                      onChange={(value) => updateTextField(activeTextId, 'fontFamily', value)}
                    />
                    <SizeControl
                      textId={activeTextId}
                      value={currentText.fontSize}
                      pendingValue={pendingFontSizeRef.current?.textId === activeTextId ? pendingFontSizeRef.current.value : currentText.fontSize}
                      disabled={!safeActivePlacementKey || !activeTextId}
                      editing={editingFontSize}
                      editValue={fontSizeInput}
                      onStartEdit={() => {
                        setFontSizeInput(String(Math.round(currentText.fontSize || 16)));
                        setEditingFontSize(true);
                      }}
                      onEditChange={(e) => setFontSizeInput(e.target.value)}
                      onEditBlur={() => {
                        let value = Number(fontSizeInput);

                        if (isNaN(value)) {
                          value = currentText.fontSize;
                        }

                        value = Math.max(10, Math.min(getMaxFontSize(), value));

                        updateTextField(activeTextId, "fontSize", value);
                        setEditingFontSize(false);

                        requestAnimationFrame(() => {
                          handleFontSizeDragEnd(activeTextId);
                        });
                      }}
                      onEditKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.target.blur();
                        }
                      }}
                      onRangeChange={(e) => {
                        updateFontSizeSmooth(
                          activeTextId,
                          Number(e.target.value)
                        );
                      }}
                      onRangePointerUp={() => {
                        flushFontSizeUpdate();

                        requestAnimationFrame(() => {
                          handleFontSizeDragEnd(activeTextId);
                        });
                      }}
                      max={
                        activePlacementKey === 'front-print'
                          ? 120
                          : activePlacementKey === 'front-embroidery'
                            ? 20
                            : activePlacementKey === 'back-embroidery'
                              ? 20
                              : activePlacementKey === 'back-print'
                                ? 90
                                : 20
                      }
                      getMaxFontSize={getMaxFontSize}
                      handleDragEnd={() => handleFontSizeDragEnd(activeTextId)}
                    />
                  </div>
                  <TextStyleControls
                    textId={activeTextId}
                    item={currentText}
                    disabled={!safeActivePlacementKey || !activeTextId}
                    onUpdate={updateTextField}
                    onTransform={updateTextTransform}
                    onAlign={updateTextAlign}
                    showUnderline={true}
                    showAlignButtons={true}
                    isNumberText={false}
                    alignDisabled={!activeTextId || !hasMultipleLines}
                  />
                  <div className="editor-row">
                    <TextColorControl
                      textId={activeTextId}
                      value={currentText.color}
                      disabled={!safeActivePlacementKey || !activeTextId}
                      onChange={(color) => updateTextField(activeTextId, 'color', color)}
                      allTextColors={allTextColors}
                      DEFAULT_TEXT_COLORS={DEFAULT_TEXT_COLORS}
                      customColors={customColors}
                      setCustomColors={setCustomColors}
                      colorInputRef={colorInputRef}
                      showCustomInput={showCustomInput}
                      setShowCustomInput={setShowCustomInput}
                    />
                  </div>
                  <TextEffectsPanel
                    textId={activeTextId}
                    item={currentText}
                    disabled={!safeActivePlacementKey || !activeTextId}
                    onUpdate={updateTextField}
                    pendingRotationRef={pendingTextRotationRef}
                    updateRotationSmooth={updateTextRotationSmooth}
                    flushRotationUpdate={flushTextRotationUpdate}
                    pendingTextRotationRef={pendingTextRotationRef}
                    flushTextRotationUpdate={flushTextRotationUpdate}
                    updateTextRotationSmooth={updateTextRotationSmooth}
                  />
                </div>
              </div>
            </div>
          )}

          {
            activeTab === 'images' &&
            <div className="images-section">
              <h3 className="section-title">Upload Images</h3>
              <div
                className={`upload-area${isDragging ? ' dragging' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragging(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragging(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragging(false);

                  const file = e.dataTransfer.files?.[0];
                  if (file && !file.type.startsWith('image/')) {
                    setUploadError('Only image files are allowed');
                    if (uploadErrorTimeoutRef.current) clearTimeout(uploadErrorTimeoutRef.current);
                    uploadErrorTimeoutRef.current = setTimeout(() => setUploadError(''), 5000);
                  } else if (file) {
                    handleAddImageToPlacement(file);
                    setUploadError('')
                  }
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && !file.type.startsWith('image/')) {
                      setUploadError('Only image files are allowed');
                      if (uploadErrorTimeoutRef.current) clearTimeout(uploadErrorTimeoutRef.current);
                      uploadErrorTimeoutRef.current = setTimeout(() => setUploadError(''), 5000);
                    } else if (file) {
                      handleAddImageToPlacement(file);
                        setUploadError('')
                    }
                    e.target.value = '';
                  }}
                />
                <svg className="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <p className="upload-text">Click to upload or drag and drop</p>
                <p className="upload-subtext">PNG, JPG up to 10MB</p>
              </div>
              {uploadError && (
                <p className="error-message" style={{ marginTop: 8 }}>{uploadError}</p>
              )}
              {/* <div className="editor-row">
                <div className="control-group">
                  <div className="control-label">
                    {currentImage ? 'Selected Image' : 'Select an image on the shirt to edit it'}
                  </div>
                </div>
              </div> */}
              <div className="editor-row">
                <div className="control-group">
                  <div className="control-label">Tools</div>
                  <div className="text-style-buttons">
                    <button
                      type="button"
                      className="toggle-button"
                      disabled={!currentImage}
                      title="Align left"
                      onClick={() => alignImageHorizontal(activeImageId, 'left')}
                    >
                      <AlignHorizontalJustifyStartIcon size={16} color="#80808082" />
                    </button>
                    <button
                      type="button"
                      className="toggle-button"
                      disabled={!currentImage}
                      title="Align center (horizontal)"
                      onClick={() => alignImageHorizontal(activeImageId, 'center')}
                    >
                      <AlignHorizontalJustifyCenterIcon size={16} color="#80808082" />
                    </button>
                    <button
                      type="button"
                      className="toggle-button"
                      disabled={!currentImage}
                      title="Align right"
                      onClick={() => alignImageHorizontal(activeImageId, 'right')}
                    >
                      <AlignHorizontalJustifyEndIcon size={16} color="#80808082" />
                    </button>
                    <button
                      type="button"
                      className="toggle-button"
                      disabled={!currentImage}
                      title="Align top"
                      onClick={() => alignImageVertical(activeImageId, 'top')}
                    >
                      <AlignVerticalJustifyStart size={16} color="#80808082" />
                    </button>
                    <button
                      type="button"
                      className="toggle-button"
                      disabled={!currentImage}
                      title="Align middle (vertical)"
                      onClick={() => alignImageVertical(activeImageId, 'center')}
                    >
                      <AlignVerticalJustifyCenter size={16} color="#80808082" />
                    </button>
                    <button
                      type="button"
                      className="toggle-button"
                      disabled={!currentImage}
                      title="Align bottom"
                      onClick={() => alignImageVertical(activeImageId, 'bottom')}
                    >
                      <AlignVerticalJustifyEnd size={16} color="#80808082" />
                    </button>
                    <button
                      type="button"
                      className="toggle-button"
                      disabled={!currentImage}
                      title="Best Fit - scale image to fill the print area"
                      onClick={() => bestFitImage(activeImageId)}
                    >
                      <Maximize2 size={14} color="#80808082" />
                    </button>
                    <button
                      type="button"
                      className="toggle-button"
                      disabled={!currentImage}
                      title="Delete image"
                      onClick={() => deleteImageItem(activeImageId)}
                    >
                      <Trash2 size={14} style={{ marginRight: 6 }} color='red' />
                    </button>
                  </div>
                </div>
              </div>

              {currentImage && (
                <div className="control-group">
                  <div className="control-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Opacity</span>
                    <button
                      type="button"
                      className="toggle-button"
                      title="Reset"
                      onClick={() => updateImageField(activeImageId, 'opacity', 1)}
                    >
                      <RotateCcw size={14} color="#80808082" />
                    </button>
                  </div>
                  <div className="range-with-value">
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={currentImage?.opacity ?? 1}
                      onChange={(e) => updateImageField(activeImageId, 'opacity', Number(e.target.value))}
                    />
                    <div className="">{Number(currentImage?.opacity ?? 1).toFixed(1)}</div>
                  </div>
                </div>
              )}

              {currentImage && (
                <div className="control-group">
                  <div className="control-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Rotation</span>
                    <button
                      type="button"
                      className="toggle-button"
                      title="Reset"
                      onClick={() => updateImageField(activeImageId, 'rotation', 0)}
                    >
                      <RotateCcw size={14} color="#80808082" />
                    </button>
                  </div>
                  <div className="range-with-value">
                    <input
                      type="range"
                      min="0"
                      max="360"
                      step="1"
                      value={
                        pendingImageRotationRef.current?.imageId === activeImageId
                          ? pendingImageRotationRef.current.value
                          : currentImage?.rotation || 0
                      }
                      onChange={(e) => {
                        updateImageRotationSmooth(
                          activeImageId,
                          Number(e.target.value)
                        );
                      }}
                      onPointerUp={() => {
                        flushImageRotationUpdate();
                      }}
                    />
                    <div className="">{Math.round(pendingImageRotationRef.current?.imageId === activeImageId ? pendingImageRotationRef.current.value : currentImage?.rotation || 0)}</div>
                  </div>
                </div>
              )}

              {currentImage && (
                <div className="control-group">
                  <div className="control-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Skew</span>
                    <button
                      type="button"
                      className="toggle-button"
                      title="Reset"
                      onClick={() => {
                        updateImageField(activeImageId, 'skewX', 0);
                        updateImageField(activeImageId, 'skewY', 0);
                      }}
                    >
                      <RotateCcw size={14} color="#80808082" />
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                    <span style={{ fontSize: 12, color: '#666', width: 56, flexShrink: 0 }}>SkewX</span>
                    <input
                      type="range"
                      min="0"
                      max="180"
                      step="1"
                      value={currentImage?.skewX || 0}
                      style={{ flex: 1 }}
                      onChange={(e) => updateImageField(activeImageId, 'skewX', Number(e.target.value))}
                    />
                    <div className="">{currentImage?.skewX || 0}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                    <span style={{ fontSize: 12, color: '#666', width: 56, flexShrink: 0 }}>SkewY</span>
                    <input
                      type="range"
                      min="0"
                      max="180"
                      step="1"
                      value={currentImage?.skewY || 0}
                      style={{ flex: 1 }}
                      onChange={(e) => updateImageField(activeImageId, 'skewY', Number(e.target.value))}
                    />
                    <div className="">{currentImage?.skewY || 0}</div>
                  </div>
                </div>
              )}
              {currentImage && (
                <ImageEffectsPanel
                  imageId={activeImageId}
                  item={currentImage}
                  disabled={!safeActivePlacementKey || !activeImageId}
                  onUpdate={updateImageField}
                />
              )}
              {currentImage && (
                <ImageFiltersPanel
                  imageId={activeImageId}
                  item={currentImage}
                  disabled={!safeActivePlacementKey || !activeImageId}
                  onUpdate={updateImageField}
                />
              )}
            </div>
          }

          {
            activeTab === 'number' &&
            <div className="number-section">
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter name..."
                  value={nameValue}
                  onFocus={() => {
                    setActiveNameNumberRole('name');
                    setActiveNameNumberId(nameNumberTextsForActivePlacement.nameItem?.id || null);
                  }}
                  onChange={(e) => setNameValue(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Number</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter number..."
                  value={numberValue}
                  onFocus={() => {
                    setActiveNameNumberRole('number');
                    setActiveNameNumberId(nameNumberTextsForActivePlacement.numberItem?.id || null);
                  }}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setNumberValue(value);
                  }}
                />
              </div>
              <button
                type="button"
                className={`border-button ${canApplyNameNumber ? '' : 'disabled'}`}
                disabled={!safeActivePlacementKey || !canApplyNameNumber}
                onClick={() => {
                  if (!safeActivePlacementKey) return;
                  let selectedIdAfterApply = null;
                  const { width: overlayWidth, height: overlayHeight } = getOverlayDims();
                  if (!overlayWidth || !overlayHeight) return;

                  setPlacementTexts((prev) => {
                    const arr = [...(prev?.[safeActivePlacementKey] || [])];

                    const padding = 8;
                    const gapPx = 6;
                    const maxFitWidth = overlayWidth - padding * 2;
                    const maxFitHeight = overlayHeight - padding * 2;

                    const hasName = nameValue.trim().length > 0;
                    const hasNumber = numberValue.trim().length > 0;

                    if (hasName && hasNumber) {
                      // Both exist — compute shared font size
                      const nameLen = nameValue.trim().length;
                      const numberLen = numberValue.trim().length;
                      const charFactor = 0.58; // Use Arial default

                      const maxWidthFs = Math.min(
                        maxFitWidth / (nameLen * charFactor),
                        maxFitWidth / (numberLen * charFactor)
                      );
                      const maxHeightFs = maxFitHeight / (1.25 + 1.25 + (gapPx / 16));

                      const fs = Math.max(10, Math.min(200, Math.floor(Math.min(maxWidthFs, maxHeightFs))));

                      const nameH = fs * 1.25;
                      const numberH = fs * 1.25;
                      const combinedH = nameH + gapPx + numberH;
                      const startY = Math.max(padding, Math.round((overlayHeight - combinedH) / 2));

                      const nameW = nameLen * fs * charFactor;
                      const numberW = numberLen * fs * charFactor;

                      upsertRoleItem(arr, "name", nameValue.trim(), {
                        fontSize: fs,
                        fontFamily: "Arial",
                        color: "#ffffff",
                        bold: false,
                        italic: false,
                        underline: false,
                        textAlign: "center",
                        textTransform: "none",
                        flipH: false,
                        flipV: false,
                        x: Math.max(padding, Math.round((overlayWidth - nameW) / 2)),
                        y: startY,
                        rotation: 0,
                      });

                      upsertRoleItem(arr, "number", numberValue.trim(), {
                        fontSize: fs,
                        fontFamily: "Arial",
                        color: "#ffffff",
                        bold: true,
                        italic: false,
                        underline: false,
                        textAlign: "center",
                        textTransform: "none",
                        flipH: false,
                        flipV: false,
                        x: Math.max(padding, Math.round((overlayWidth - numberW) / 2)),
                        y: startY + nameH + gapPx,
                        rotation: 0,
                      });
                      const numberItem = arr.find(item => item.role === 'number');
                      if (numberItem) selectedIdAfterApply = numberItem.id;
                    } else if (hasName) {
                      // Only name — center it vertically
                      const nameLen = nameValue.trim().length;
                      const charFactor = 0.58;
                      const maxWidthFs = maxFitWidth / (nameLen * charFactor);
                      const maxHeightFs = maxFitHeight / 1.25;
                      const fs = Math.max(10, Math.min(200, Math.floor(Math.min(maxWidthFs, maxHeightFs))));

                      const nameW = nameLen * fs * charFactor;
                      const nameH = fs * 1.25;

                      upsertRoleItem(arr, "name", nameValue.trim(), {
                        fontSize: fs,
                        fontFamily: "Arial",
                        color: "#ffffff",
                        bold: false,
                        italic: false,
                        underline: false,
                        textAlign: "center",
                        textTransform: "none",
                        flipH: false,
                        flipV: false,
                        x: Math.max(padding, Math.round((overlayWidth - nameW) / 2)),
                        y: Math.max(padding, Math.round((overlayHeight - nameH) / 2)),
                        rotation: 0,
                      });
                      const nameItem = arr.find(item => item.role === 'name');
                      if (nameItem) selectedIdAfterApply = nameItem.id;
                    } else if (hasNumber) {
                      // Only number — center it vertically
                      const numberLen = numberValue.trim().length;
                      const charFactor = 0.58;
                      const maxWidthFs = maxFitWidth / (numberLen * charFactor);
                      const maxHeightFs = maxFitHeight / 1.25;
                      const fs = Math.max(10, Math.min(200, Math.floor(Math.min(maxWidthFs, maxHeightFs))));

                      const numberW = numberLen * fs * charFactor;
                      const numberH = fs * 1.25;

                      upsertRoleItem(arr, "number", numberValue.trim(), {
                        fontSize: fs,
                        fontFamily: "Arial",
                        color: "#ffffff",
                        bold: true,
                        italic: false,
                        underline: false,
                        textAlign: "center",
                        textTransform: "none",
                        flipH: false,
                        flipV: false,
                        x: Math.max(padding, Math.round((overlayWidth - numberW) / 2)),
                        y: Math.max(padding, Math.round((overlayHeight - numberH) / 2)),
                        rotation: 0,
                      });
                      const numberItem = arr.find(item => item.role === 'number');
                      if (numberItem) selectedIdAfterApply = numberItem.id;
                    }

                    return {
                      ...prev,
                      [safeActivePlacementKey]: arr,
                    };
                  });

                  if (selectedIdAfterApply) {
                    setSelectedTextId?.(selectedIdAfterApply);
                    setActiveTextId?.(selectedIdAfterApply);
                    setActiveNameNumberId(selectedIdAfterApply);
                  }
                  setSelectedImageId?.(null);
                  setActiveImageId?.(null);
                }}
              >
                Apply
              </button>
              <div style={{ marginTop: 16, display: 'grid', gap: 12 }}>
                <div className="editor-row">
                  <TextToolsPanel
                    textId={activeRoleItemId}
                    item={currentRoleText}
                    disabled={!activeTextId}
                    onAlignHorizontal={alignTextHorizontal}
                    onAlignVertical={alignTextVertical}
                    onFlip={updateTextFlip}
                    onBestFit={bestFitText}
                    onDelete={() => {
                      if (!activeRoleItemId) return;
                      deleteTextItem(activeRoleItemId);
                      setActiveNameNumberId(null);
                      if (activeNameNumberRole === 'name') setNameValue('');
                      if (activeNameNumberRole === 'number') setNumberValue('');
                    }}
                  />
                </div>
                <div className="editor-row">
                  <CurveControl
                    textId={activeRoleItemId}
                    curve={currentRoleText?.curve || false}
                    arcDirection={currentRoleText.arcDirection || 'up'}
                    disabled={!activeTextId}
                    onToggleCurve={(textId, checked) => updateTextField(textId, 'curve', checked)}
                    onSetDirection={(textId, direction) => updateTextField(textId, 'arcDirection', direction)}
                  />
                </div>
                <FontSelect
                  value={currentRoleText.fontFamily}
                  disabled={!activeTextId}
                  onChange={(value) => updateTextField(activeRoleItemId, 'fontFamily', value)}
                />
                <SizeControl
                  textId={activeRoleItemId}
                  value={currentRoleText.fontSize}
                  pendingValue={pendingRoleFontSizeRef.current?.textId === activeRoleItemId ? pendingRoleFontSizeRef.current.value : currentRoleText.fontSize}
                  disabled={!activeTextId}
                  editing={editingRoleFontSize}
                  editValue={roleFontSizeInput}
                  onStartEdit={() => {
                    if (!activeRoleItemId) return;
                    setRoleFontSizeInput(
                      String(Math.round(currentRoleText?.fontSize || 24))
                    );
                    setEditingRoleFontSize(true);
                  }}
                  onEditChange={(e) => setRoleFontSizeInput(e.target.value)}
                  onEditBlur={() => {
                    let value = Number(roleFontSizeInput);
                    if (isNaN(value)) {
                      value = currentRoleText.fontSize;
                    }
                    value = Math.max(10, Math.min(getMaxFontSize(), value));
                    updateTextField(activeRoleItemId, "fontSize", value);
                    setEditingRoleFontSize(false);
                  }}
                  onEditKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.target.blur();
                    }
                  }}
                  onRangeChange={(e) => {
                    updateRoleFontSizeSmooth(
                      activeRoleItemId,
                      Number(e.target.value)
                    );
                  }}
                  onRangePointerUp={() => {
                    flushRoleFontSizeUpdate();
                    requestAnimationFrame(() => {
                      handleRoleFontSizeDragEnd(activeRoleItemId);
                    });
                  }}
                  max={
                    activePlacementKey === 'front-print'
                      ? 120
                      : activePlacementKey === 'front-embroidery'
                        ? 20
                        : activePlacementKey === 'back-embroidery'
                          ? 20
                          : activePlacementKey === 'back-print'
                            ? 90
                            : 20
                  }
                  getMaxFontSize={getMaxFontSize}
                />
                <div className="editor-row">
                  <TextColorControl
                    textId={activeRoleItemId}
                    value={currentRoleText.color}
                    disabled={!activeTextId}
                    onChange={(color) => updateTextField(activeRoleItemId, 'color', color)}
                    allTextColors={allTextColors}
                    DEFAULT_TEXT_COLORS={DEFAULT_TEXT_COLORS}
                    customColors={customColors}
                    setCustomColors={setCustomColors}
                    colorInputRef={colorInputRef}
                    showCustomInput={showCustomInput}
                    setShowCustomInput={setShowCustomInput}
                  />
                </div>
                <TextStyleControls
                  textId={activeRoleItemId}
                  item={currentRoleText}
                  disabled={!activeTextId}
                  onUpdate={updateTextField}
                  onTransform={updateTextTransform}
                  onAlign={updateTextAlign}
                  showUnderline={false}
                  showAlignButtons={false}
                  isNumberText={isNumberText}
                  buttonsClassName="toolbar-group"
                />
              </div>
              <TextEffectsPanel
                textId={activeRoleItemId}
                item={currentRoleText}
                disabled={!activeTextId}
                onUpdate={updateTextField}
                pendingRotationRef={pendingTextRotationRef}
                updateRotationSmooth={updateTextRotationSmooth}
                flushRotationUpdate={flushTextRotationUpdate}
                pendingTextRotationRef={pendingTextRotationRef}
                flushTextRotationUpdate={flushTextRotationUpdate}
                updateTextRotationSmooth={updateTextRotationSmooth}
              />
            </div>
          }
        </div>
      </div>
    </div >
  );
};

export default LeftPanel;